import React, { useState, useEffect } from 'react';
import { DocumentChartBarIcon, ArrowDownTrayIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useInventory } from '../../../contexts/InventoryContext';
import CommonDataTable from '../../Common/CommonDataTable';
import CustomSelect from '../../Common/CustomSelect';
import LoadingSpinner from '../../Common/LoadingSpinner';
import { inventoryAPI } from '../../../services/inventoryApi';

const BincardReport = () => {
  const { items, warehouses, fetchItems, fetchWarehouses } = useInventory();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    itemId: '',
    itemIds: [],
    warehouseId: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: 100,
    sortBy: 'transactionDate',
    sortOrder: 'asc'
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [multipleItemsMode, setMultipleItemsMode] = useState(false);

  useEffect(() => {
    if (fetchItems) {
      fetchItems();
    }
    if (fetchWarehouses) {
      fetchWarehouses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filters.itemId || filters.itemIds.length > 0) {
      loadBincardReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadBincardReport = async () => {
    if (!filters.itemId && filters.itemIds.length === 0) {
      return;
    }

    setLoading(true);
    try {
      const params = {
        ...filters,
        itemIds: filters.itemIds.length > 0 ? filters.itemIds.join(',') : undefined
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await inventoryAPI.getBincardReport(params);

      if (response.success) {
        setReportData(response.data.bincardData || []);
        setSummary(response.data.summary);
      } else {
        console.error('Failed to load bincard report:', response.message);
        setReportData([]);
        setSummary(null);
      }
    } catch (error) {
      console.error('Error loading bincard report:', error);
      setReportData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1
    }));
  };

  const handleItemSelection = (itemId) => {
    if (multipleItemsMode) {
      const newSelectedItems = selectedItems.includes(itemId)
        ? selectedItems.filter(id => id !== itemId)
        : [...selectedItems, itemId];

      setSelectedItems(newSelectedItems);
      setFilters(prev => ({
        ...prev,
        itemIds: newSelectedItems,
        itemId: ''
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        itemId: itemId,
        itemIds: []
      }));
      setSelectedItems([]);
    }
  };

  const clearFilters = () => {
    setFilters({
      itemId: '',
      itemIds: [],
      warehouseId: '',
      dateFrom: '',
      dateTo: '',
      page: 1,
      limit: 100,
      sortBy: 'transactionDate',
      sortOrder: 'asc'
    });
    setSelectedItems([]);
    setMultipleItemsMode(false);
    setReportData([]);
    setSummary(null);
  };

  const exportToCSV = async () => {
    if (!filters.itemId && filters.itemIds.length === 0) {
      alert('Please select at least one item before exporting.');
      return;
    }

    setExporting(true);
    try {
      const params = {
        ...filters,
        itemIds: filters.itemIds.length > 0 ? filters.itemIds.join(',') : undefined,
        format: 'csv'
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await inventoryAPI.getBincardReportCSV(params);

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bincard-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting bincard report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const getMovementTypeBadge = (type, direction) => {
    const typeConfig = {
      'receipt': 'bg-green-100 text-green-800',
      'issue': 'bg-red-100 text-red-800',
      'transfer_in': 'bg-blue-100 text-blue-800',
      'transfer_out': 'bg-orange-100 text-orange-800',
      'adjustment_in': 'bg-emerald-100 text-emerald-800',
      'adjustment_out': 'bg-rose-100 text-rose-800',
      'return_in': 'bg-purple-100 text-purple-800',
      'return_out': 'bg-pink-100 text-pink-800'
    };

    const color = typeConfig[type] || 'bg-gray-100 text-gray-800';
    const label = (type || 'Unknown').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {direction === 'in' ? '↑' : '↓'} {label}
      </span>
    );
  };

  const getDirectionBadge = (direction, quantity) => {
    const isInbound = direction === 'in';
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
        isInbound ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {isInbound ? '+' : '-'}{quantity}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      key: 'transactionDate',
      header: 'Date',
      render: (value) => (
        <span className="text-sm">{formatDate(value)}</span>
      )
    },
    {
      key: 'item',
      header: 'Item',
      render: (item) => (
        <div>
          <div className="font-medium text-gray-900">{item?.name}</div>
          <div className="text-sm text-gray-500">{item?.sku}</div>
        </div>
      )
    },
    {
      key: 'warehouse',
      header: 'Warehouse',
      render: (warehouse) => (
        <div>
          <div className="font-medium text-gray-900">{warehouse?.name}</div>
          <div className="text-sm text-gray-500">{warehouse?.code}</div>
        </div>
      )
    },
    {
      key: 'movementType',
      header: 'Type',
      render: (type, row) => getMovementTypeBadge(type, row.direction)
    },
    {
      key: 'quantity',
      header: 'Qty Change',
      render: (quantity, row) => getDirectionBadge(row.direction, quantity)
    },
    {
      key: 'unitCost',
      header: 'Unit Cost',
      render: (value) => parseFloat(value).toFixed(2)
    },
    {
      key: 'totalCost',
      header: 'Total Cost',
      render: (value) => parseFloat(value).toFixed(2)
    },
    {
      key: 'balanceBefore',
      header: 'Balance Before',
      render: (value) => (
        <span className="font-mono text-sm">{value || 0}</span>
      )
    },
    {
      key: 'balanceAfter',
      header: 'Balance After',
      render: (value) => (
        <span className="font-mono text-sm font-medium">{value || 0}</span>
      )
    },
    {
      key: 'referenceDocument',
      header: 'Reference',
      render: (ref) => ref?.number ? (
        <span className="text-sm text-blue-600">{ref.number}</span>
      ) : (
        <span className="text-sm text-gray-400">-</span>
      )
    }
  ];

  const hasSelection = !!filters.itemId || filters.itemIds.length > 0;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">

      {/* Filters Card */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Card header */}
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-5 py-3 flex items-center gap-3 rounded-t-xl">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <DocumentChartBarIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Bincard Report</h2>
              <p className="text-indigo-200 text-xs">Complete transaction history for inventory items</p>
            </div>
          </div>

          <div className="px-5 py-4">
            {/* Item Selection Mode Toggle */}
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={multipleItemsMode}
                  onChange={(e) => {
                    setMultipleItemsMode(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedItems([]);
                      setFilters(prev => ({ ...prev, itemIds: [] }));
                    }
                  }}
                  className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Select multiple items</span>
              </label>
            </div>

            <div className="flex flex-wrap items-end gap-4">
              {/* Item Selection */}
              <div className={multipleItemsMode ? 'w-full md:w-auto md:min-w-[320px]' : 'flex flex-col gap-1'}>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {multipleItemsMode ? 'Items' : 'Item'} <span className="text-red-500">*</span>
                </label>
                {multipleItemsMode ? (
                  <div className="mt-1 border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto bg-gray-50">
                    {items?.map(item => (
                      <label key={item._id} className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item._id)}
                          onChange={() => handleItemSelection(item._id)}
                          className="mr-2 h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="text-sm">{item.name} ({item.sku})</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <CustomSelect
                    value={filters.itemId}
                    onChange={(e) => handleItemSelection(e.target.value)}
                    options={[
                      { value: '', label: 'Select an item...' },
                      ...(items?.map(item => ({ value: item._id, label: `${item.name} (${item.sku})` })) || [])
                    ]}
                    required
                  />
                )}
              </div>

              {/* Warehouse Filter */}
              <div className="flex flex-col gap-1 min-w-[180px]">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Warehouse</label>
                <CustomSelect
                  value={filters.warehouseId}
                  onChange={(e) => handleFilterChange('warehouseId', e.target.value)}
                  options={[
                    { value: '', label: 'All Warehouses' },
                    ...((Array.isArray(warehouses) ? warehouses : []).map(wh => ({ value: wh._id, label: `${wh.name} (${wh.code})` })))
                  ]}
                />
              </div>

              {/* Date From */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5 text-indigo-400" />
                  Date From
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[160px]"
                />
              </div>

              {/* Date To */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5 text-indigo-400" />
                  Date To
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[160px]"
                />
              </div>

              {/* Sort By */}
              <div className="flex flex-col gap-1 min-w-[160px]">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sort By</label>
                <CustomSelect
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  options={[
                    { value: 'transactionDate', label: 'Transaction Date' },
                    { value: 'movementNumber', label: 'Movement Number' },
                    { value: 'movementType', label: 'Movement Type' },
                    { value: 'quantity', label: 'Quantity' }
                  ]}
                />
              </div>

              {/* Sort Order */}
              <div className="flex flex-col gap-1 min-w-[140px]">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sort Order</label>
                <CustomSelect
                  value={filters.sortOrder}
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                  options={[
                    { value: 'asc', label: 'Ascending' },
                    { value: 'desc', label: 'Descending' }
                  ]}
                />
              </div>

              {/* Records per Page */}
              <div className="flex flex-col gap-1 min-w-[120px]">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Per Page</label>
                <CustomSelect
                  value={filters.limit}
                  onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                  options={[
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                    { value: 250, label: '250' },
                    { value: 500, label: '500' }
                  ]}
                />
              </div>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-800 transition-colors flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset
                </button>
                <button
                  type="button"
                  onClick={exportToCSV}
                  disabled={exporting || !hasSelection}
                  className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 shadow-sm"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  {exporting ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Spinner */}
      {loading && (
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner size="lg" fullScreen={false} />
        </div>
      )}

      {/* Content */}
      {!loading && (
        <div className="flex-grow overflow-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="space-y-4">
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Transactions</h3>
                  <p className="text-2xl font-bold text-indigo-600">{summary.totalTransactions}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Inbound</h3>
                  <p className="text-2xl font-bold text-green-600">{parseFloat(summary.totalInbound).toFixed(2)}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Outbound</h3>
                  <p className="text-2xl font-bold text-red-600">{summary.totalOutbound}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Value</h3>
                  <p className="text-2xl font-bold text-purple-600">{summary.totalValue.toFixed(2)}</p>
                </div>
              </div>
            )}

            {/* Report Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-indigo-600 px-4 py-3 flex items-center justify-between">
                <span className="text-sm font-medium text-white">
                  Transaction History {reportData.length > 0 ? `(${reportData.length})` : ''}
                </span>
              </div>
              <CommonDataTable
                data={reportData}
                columns={columns}
                loading={loading}
                searchPlaceholder="Search transactions..."
                emptyMessage={
                  !hasSelection
                    ? 'Please select an item to view its transaction history.'
                    : 'No transactions found for the selected criteria.'
                }
                pageSize={filters.limit}
                showSelection={false}
                showSearch={true}
                showPagination={true}
                sortable={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BincardReport;
